/**
 * NIP-56 Reporting utilities (kind 1984).
 *
 * Reports reference content via an `e` tag (events) or `p` tag (profiles),
 * with the report type as the 3rd entry on the tag.
 */

import type { NostrEvent } from '@nostrify/nostrify';
import type { VideoData } from '@/lib/videoTypes';

/** Report types defined by NIP-56. */
export type ReportType =
  | 'nudity'
  | 'malware'
  | 'profanity'
  | 'illegal'
  | 'spam'
  | 'impersonation'
  | 'other';

export const REPORT_TYPES: ReportType[] = [
  'nudity',
  'malware',
  'profanity',
  'illegal',
  'spam',
  'impersonation',
  'other',
];

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  nudity: 'Nudity / Sexual content',
  malware: 'Malware / Virus',
  profanity: 'Profanity / Hate speech',
  illegal: 'Illegal content',
  spam: 'Spam',
  impersonation: 'Impersonation',
  other: 'Other',
};

export const REPORT_TYPE_DESCRIPTIONS: Record<ReportType, string> = {
  nudity: 'Depictions of nudity, porn, or other sexual content.',
  malware: 'Virus, trojan, spyware, ransomware, or other malicious software.',
  profanity: 'Profanity, hateful speech, or abusive language.',
  illegal: 'Content that may be illegal in some jurisdictions.',
  spam: 'Unsolicited, repetitive, or misleading content.',
  impersonation: 'Someone pretending to be another person.',
  other: 'Content that does not fit the above categories.',
};

/** Aggregate report data for feeds. */
export interface ReportsData {
  /** Event IDs that have at least one report. */
  reportedIds: Set<string>;
  /** Addresses (kind:pubkey:d) that have at least one report. */
  reportedAddresses: Set<string>;
  /** Pubkeys that have at least one report. */
  reportedPubkeys: Set<string>;
}

export const EMPTY_REPORTS: ReportsData = {
  reportedIds: new Set(),
  reportedAddresses: new Set(),
  reportedPubkeys: new Set(),
};

/** Build ReportsData from raw kind 1984 events. */
export function buildReportsData(events: NostrEvent[]): ReportsData {
  const reportedIds = new Set<string>();
  const reportedAddresses = new Set<string>();
  const reportedPubkeys = new Set<string>();

  for (const event of events) {
    if (event.kind !== 1984) continue;
    for (const tag of event.tags) {
      if (tag[0] === 'e' && tag[1]) reportedIds.add(tag[1]);
      if (tag[0] === 'a' && tag[1]) reportedAddresses.add(tag[1]);
      if (tag[0] === 'p' && tag[1]) reportedPubkeys.add(tag[1]);
    }
  }

  return { reportedIds, reportedAddresses, reportedPubkeys };
}

/** Check whether a video has been reported. */
export function isVideoReported(video: VideoData, reports: ReportsData): boolean {
  if (reports.reportedIds.has(video.id)) return true;
  if (video.dTag && reports.reportedAddresses.has(`${video.originalEvent.kind}:${video.pubkey}:${video.dTag}`)) return true;
  if (reports.reportedPubkeys.has(video.pubkey)) return true;
  return false;
}

/** Build the tags for a kind 1984 report of a video. */
export function buildVideoReportTags(video: VideoData, type: ReportType): string[][] {
  const tags: string[][] = [
    ['e', video.id, '', type],
    ['p', video.pubkey, '', type],
    ['k', String(video.originalEvent.kind)],
  ];
  if (video.dTag) {
    tags.push(['a', `${video.originalEvent.kind}:${video.pubkey}:${video.dTag}`, '', type]);
  }
  return tags;
}
